import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ChessIcon from '@mui/icons-material/Casino';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useProgrammation } from '../../hooks/useProgrammation';
import type { Period, Theme } from '../../types/programmation';
import type { SelectChangeEvent } from '@mui/material';

// Étendre Theme pour gérer customLabel
interface ExtendedTheme extends Theme {
  customLabel: string;
}

// Étendre Period pour utiliser ExtendedTheme
type ExtendedPeriod = Omit<Period, 'themes'> & {
  title?: string;
  themes: Record<keyof Period['themes'], [ExtendedTheme, ExtendedTheme]>;
};

type ProgrammationFormData = {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  category: string;
  periods: ExtendedPeriod[];
};

// Suggestions de labels
const THEME_LABELS: Record<keyof Period['themes'], string[]> = {
  tactique: [
    // Principes fondamentaux
    'Défendre son but',
    'Conserver le ballon',
    'Progresser',
    'Finir (marquer)',
    'Empêcher la progression adverse',
    // Thèmes tactiques spécifiques
    'Jeu dans les couloirs',
    'Transition offensive',
    'Transition défensive',
    'Pressing haut',
    'Bloc bas',
    'Jeu en triangle',
    'Surcharge côté ballon',
    'Dézonage / Appels en profondeur',
    'Relance courte',
    'Marquage et couverture',
    'Occupation de l\'espace',
    'Sortie de balle',
    'Contre-attaque',
    'Défense en infériorité',
    'Gestion du temps de jeu',
    'Variation du tempo',
    'Organisation offensive / défensive',
    'Changement d\'aile',
    'Fixer - Renverser - Exploiter',
    'Zones de danger',
    'Repli défensif',
    // Thèmes tactiques existants
    'Défense de zone',
    'Jeu en contre',
    'Construction depuis l\'arrière',
    'Défense individuelle',
    'Bloc médian'
  ],
  technique: [
    'Passe courte',
    'Finition',
    'Dribble 1v1',
    'Contrôle orienté',
    'Jonglage / Maîtrise du ballon',
    'Conduite de balle',
    'Feintes et changements de direction',
    'Jeu à 2 / une-deux',
    'Jeu dos au but',
    'Centres et reprises',
    'Têtes offensives et défensives',
    'Dégagements / relances du gardien',
    'Rebond aérien',
    'Sortie de pression',
    'Technique de tacle / interception',
    // Thèmes techniques existants
    'Passes courtes',
    'Centres',
    'Dribbles',
    'Jeu de tête'
  ],
  athletique: [
    'Vitesse',
    'Coordination',
    'Endurance fondamentale',
    'Puissance',
    'Force explosive',
    'Réactivité',
    'Appuis / changements de direction',
    'Agilité',
    'Résistance à la fatigue',
    'Prévention des blessures',
    'Renforcement du tronc',
    'Travail pliométrique',
    'Sprint-récupération',
    'Mobilité articulaire',
    'Gainage dynamique',
    // Thèmes athlétiques existants
    'Endurance',
    'Force',
    'Explosivité',
    'Récupération'
  ],
  mental: [
    'Concentration',
    'Motivation',
    'Confiance en soi',
    'Gestion des émotions',
    'Esprit d\'équipe',
    'Prise de décision',
    'Résilience / gestion de l\'échec',
    'Communication sur le terrain',
    'Respect des consignes',
    'Autonomie',
    'Leadership',
    'Visualisation positive',
    'Maîtrise du stress',
    'Engagement / intensité',
    'Anticipation mentale'
  ],
};
const themeKeys = Object.keys(THEME_LABELS) as Array<keyof Period['themes']>;

const EditProgrammation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { programmation, loading, error, updateProgrammation } = useProgrammation(id!);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const emptyTheme = useCallback<() => ExtendedTheme>(
    () => ({ weeks: 1, sessions: 1, label: '', customLabel: '' }),
    []
  );

  const emptyPeriod = useCallback<() => ExtendedPeriod>(
    () => ({
      title: '',
      comments: '',
      weeks: 1,
      sessions: 1,
      themes: themeKeys.reduce((acc, key) => {
        acc[key] = [emptyTheme(), emptyTheme()];
        return acc;
      }, {} as Record<keyof Period['themes'], [ExtendedTheme, ExtendedTheme]>),
    }),
    [emptyTheme]
  );

  const [formData, setFormData] = useState<ProgrammationFormData>({
    title: '',
    description: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    category: 'senior',
    periods: [emptyPeriod()],
  });
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (programmation) {
      try {
        setFormData({
          title: programmation.title,
          description: programmation.description || '',
          startDate: format(programmation.startDate, 'yyyy-MM-dd'),
          endDate: format(programmation.endDate, 'yyyy-MM-dd'),
          category: programmation.category || 'senior',
          periods: programmation.periods.map(p => {
            // Créer une structure de thèmes sécurisée
            const safeThemes = {} as Record<keyof Period['themes'], [ExtendedTheme, ExtendedTheme]>;
            
            // Pour chaque clé de thème (tactique, technique, etc.)
            themeKeys.forEach(key => {
              // Récupérer les thèmes existants ou créer un tableau par défaut
              const themeArray = Array.isArray(p.themes[key]) ? p.themes[key] : [emptyTheme(), emptyTheme()];
              
              // S'assurer qu'il y a exactement 2 thèmes par catégorie
              safeThemes[key] = [
                { ...themeArray[0] || emptyTheme(), customLabel: '' },
                { ...themeArray[1] || emptyTheme(), customLabel: '' }
              ] as [ExtendedTheme, ExtendedTheme];
            });
            
            return {
              ...p,
              themes: safeThemes
            } as ExtendedPeriod;
          }),
        });
      } catch (error) {
        console.error('Erreur lors de l\'initialisation des données:', error);
        // Initialiser avec des données par défaut en cas d'erreur
        setFormData({
          title: '',
          description: '',
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
          category: 'senior',
          periods: [emptyPeriod()],
        });
      }
    }
  }, [programmation, emptyPeriod, emptyTheme, themeKeys]);

  // Handlers génériques
  const handleInputChange = (
    field: keyof Omit<ProgrammationFormData, 'periods'>
  ) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    setFormData(prev => ({ ...prev, [field]: (e.target as HTMLInputElement).value }));
  };

  const handlePeriodChange = (
    idx: number,
    field: keyof ExtendedPeriod,
    value: any
  ) => {
    setFormData(prev => {
      const periods = [...prev.periods];
      (periods[idx] as any)[field] = value;
      return { ...prev, periods };
    });
  };

  const handleThemeChange = (
    periodIdx: number,
    themeKey: keyof ExtendedPeriod['themes'],
    themeIdx: number,
    field: keyof ExtendedTheme,
    value: any
  ) => {
    setFormData(prev => {
      const periods = [...prev.periods];
      const themes = [...periods[periodIdx].themes[themeKey]];
      (themes[themeIdx] as any)[field] = value;
      periods[periodIdx].themes[themeKey] = themes as [ExtendedTheme, ExtendedTheme];
      return { ...prev, periods };
    });
  };

  const addPeriod = () =>
    setFormData(prev => ({ ...prev, periods: [...prev.periods, emptyPeriod()] }));

  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setSubmitError('La date de fin doit être postérieure à la date de début.');
      return;
    }
    try {
      setIsSubmitting(true);
      setSubmitError('');
      await updateProgrammation(id!, {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
      });
      // On reste sur la page d'édition après sauvegarde
    } catch {
      setSubmitError("Erreur lors de la mise à jour de la programmation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
      <CircularProgress />
    </Box>
  );

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Paper className="w-full mx-auto p-3 sm:p-6 mt-2 mb-4 bg-white shadow-md rounded-lg">
        {submitError && <Alert severity="error" className="mb-4">{submitError}</Alert>}
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
            <Typography variant="h6" className="text-gray-800 text-lg sm:text-xl font-semibold">Informations générales</Typography>
            <Box className="flex items-center gap-2">
              <Button
                type="button"
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                size={isMobile ? "medium" : "small"}
                onClick={() => navigate(-1)}
                sx={{
                  borderRadius: '8px',
                  padding: isMobile ? '8px 16px' : '6px 12px',
                  textTransform: 'none',
                  fontWeight: '600',
                }}
              >
                Retour
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                size={isMobile ? "medium" : "small"}
                sx={{
                  borderRadius: '8px',
                  padding: isMobile ? '8px 16px' : '6px 12px',
                  textTransform: 'none',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Enregistrer
              </Button>
            </Box>
          </div>
          
          {/* Ligne 1: Titre (60%) et Catégorie (40%) sur la même ligne */}
          <Box className="mb-4">
            <div className="flex flex-col sm:flex-row sm:space-x-4 w-full items-center">
              <div className="w-full sm:w-3/5 mb-4 sm:mb-0">
                <TextField
                  fullWidth
                  label="Titre"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  required
                  size={isMobile ? "medium" : "small"}
                  sx={{ 
                    '& .MuiInputBase-root': { 
                      fontSize: isMobile ? '1rem' : 'inherit',
                      height: isMobile ? '56px' : 'inherit'
                    } 
                  }}
                />
              </div>
              <div className="w-full sm:w-2/5">
                <div className="relative w-full">
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 20 20\"%3E%3Cpath stroke=\"%239ca3af\" strokeLinecap=\"round\" strokeLinejoin=\"round\" strokeWidth=\"1.5\" d=\"M6 8l4 4 4-4\"%2F%3E%3C%2Fsvg%3E")',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem',
                      cursor: 'pointer',
                      minHeight: '2.5rem',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none',
                      height: '40px'
                    }}
                  >
                    <optgroup label="Séniors" className="bg-white text-gray-800 font-bold">
                      <option value="senior" className="bg-white hover:bg-blue-100">Séniors</option>
                    </optgroup>
                    <optgroup label="U19 - U16" className="text-blue-700 font-medium">
                      <option value="u19" className="bg-white hover:bg-blue-100">U19</option>
                      <option value="u18" className="bg-white hover:bg-blue-100">U18</option>
                      <option value="u17" className="bg-white hover:bg-blue-100">U17</option>
                      <option value="u16" className="bg-white hover:bg-blue-100">U16</option>
                    </optgroup>
                    <optgroup label="U15 - U12" className="text-gray-800 font-medium">
                      <option value="u15" className="bg-white hover:bg-blue-100">U15</option>
                      <option value="u14" className="bg-white hover:bg-blue-100">U14</option>
                      <option value="u13" className="bg-white hover:bg-blue-100">U13</option>
                      <option value="u12" className="bg-white hover:bg-blue-100">U12</option>
                    </optgroup>
                    <optgroup label="U11 - U8" className="text-gray-800 font-medium">
                      <option value="u11" className="bg-white hover:bg-blue-100">U11</option>
                      <option value="u10" className="bg-white hover:bg-blue-100">U10</option>
                      <option value="u9" className="bg-white hover:bg-blue-100">U9</option>
                      <option value="u8" className="bg-white hover:bg-blue-100">U8</option>
                    </optgroup>
                  </select>
                </div>
              </div>
            </div>
          </Box>
            
          {/* Ligne 2: Description */}
          <Box className="mb-4 w-full">
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={isMobile ? 4 : 3}
              value={formData.description}
              onChange={handleInputChange('description')}
              size={isMobile ? "medium" : "small"}
              sx={{ 
                width: '100%',
                '& .MuiInputBase-root': { 
                  fontSize: isMobile ? '1rem' : 'inherit'
                } 
              }}
            />
          </Box>
            
          {/* Ligne 3: Dates sur la même ligne */}
          <Box className="mb-4">
            <div className="flex flex-col sm:flex-row sm:space-x-4 w-full">
              <div className="w-full sm:w-1/2 mb-4 sm:mb-0">
                <TextField
                  fullWidth
                  label="Date de début"
                  type="date"
                  value={formData.startDate}
                  onChange={handleInputChange('startDate')}
                  InputLabelProps={{ shrink: true }}
                  required
                  size={isMobile ? "medium" : "small"}
                  sx={{ 
                    '& .MuiInputBase-root': { 
                      fontSize: isMobile ? '1rem' : 'inherit',
                      height: isMobile ? '56px' : 'inherit'
                    } 
                  }}
                />
              </div>
              <div className="w-full sm:w-1/2">
                <TextField
                  fullWidth
                  label="Date de fin"
                  type="date"
                  value={formData.endDate}
                  onChange={handleInputChange('endDate')}
                  InputLabelProps={{ shrink: true }}
                  required
                  size={isMobile ? "medium" : "small"}
                  sx={{ 
                    '& .MuiInputBase-root': { 
                      fontSize: isMobile ? '1rem' : 'inherit',
                      height: isMobile ? '56px' : 'inherit'
                    } 
                  }}
                />
              </div>
            </div>
          </Box>

        <Box className="flex justify-center mt-4 sm:mt-6">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleIcon />}
            onClick={addPeriod}
            fullWidth={isMobile}
            sx={{
              borderRadius: '8px',
              padding: isMobile ? '12px 16px' : '8px 16px',
              fontSize: isMobile ? '1rem' : '0.875rem',
              height: isMobile ? '56px' : 'auto',
              textTransform: 'none'
            }}
          >
            Ajouter une période
          </Button>
        </Box>

        {formData.periods.map((period, pIdx) => (
          <Box key={pIdx} className={`mb-6 sm:mb-8 p-3 sm:p-4 border border-blue-200 rounded-lg bg-blue-50 ${isMobile ? 'mx-0' : ''}`}>
            <Box className="flex items-center gap-2 mb-4">
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  fullWidth
                  label="Titre de la période"
                  placeholder="Ex: Période 1 - Préparation"
                  value={period.title || ''}
                  onChange={(e) => handlePeriodChange(pIdx, 'title', e.target.value)}
                  size={isMobile ? "medium" : "small"}
                  sx={{ 
                    '& .MuiInputBase-root': { 
                      fontSize: isMobile ? '1rem' : 'inherit',
                      height: isMobile ? '56px' : 'inherit'
                    } 
                  }}
                />
              </Box>
              <IconButton
                color="error"
                size={isMobile ? "medium" : "small"}
                onClick={() => {
                  setPeriodToDelete(pIdx);
                  setDeleteDialogOpen(true);
                }}
                sx={{
                  height: isMobile ? '56px' : '40px',
                  width: isMobile ? '56px' : '40px',
                  mt: isMobile ? '8px' : '0'
                }}
              >
                <DeleteIcon fontSize={isMobile ? "medium" : "small"} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 2 : 2 }}>
              <Box sx={{ width: { xs: '50%', sm: '16.67%' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePeriodChange(pIdx, 'sessions', Math.max(1, (period.sessions || 1) - 1))}
                    sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px' }}
                  >
                    -
                  </IconButton>
                  <TextField
                    fullWidth
                    label="Séances"
                    type="number"
                    inputProps={{ min: 1, style: { textAlign: 'center' } }}
                    value={period.sessions}
                    onChange={(e) => handlePeriodChange(pIdx, 'sessions', Number(e.target.value) || 1)}
                    size={isMobile ? "medium" : "small"}
                    sx={{ 
                      '& .MuiInputBase-root': { 
                        fontSize: isMobile ? '1rem' : 'inherit',
                        height: isMobile ? '56px' : 'inherit'
                      } 
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handlePeriodChange(pIdx, 'sessions', (period.sessions || 1) + 1)}
                    sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px' }}
                  >
                    +
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ width: { xs: '50%', sm: '16.67%' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePeriodChange(pIdx, 'weeks', Math.max(1, (period.weeks || 1) - 1))}
                    sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px' }}
                  >
                    -
                  </IconButton>
                  <TextField
                    fullWidth
                    label="Semaines"
                    type="number"
                    inputProps={{ min: 1, style: { textAlign: 'center' } }}
                    value={period.weeks}
                    onChange={(e) => handlePeriodChange(pIdx, 'weeks', Number(e.target.value) || 1)}
                    size={isMobile ? "medium" : "small"}
                    sx={{ 
                      '& .MuiInputBase-root': { 
                        fontSize: isMobile ? '1rem' : 'inherit',
                        height: isMobile ? '56px' : 'inherit'
                      } 
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handlePeriodChange(pIdx, 'weeks', (period.weeks || 1) + 1)}
                    sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px' }}
                  >
                    +
                  </IconButton>
                </Box>
              </Box>
            </Box>
            <Typography variant="subtitle1" className="mt-4">Thèmes</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: isMobile ? 1 : 2 }}>
              {themeKeys.map((themeKey) => (
                <Box key={themeKey} className="mb-4 pb-2 border-b border-blue-100" sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 2 }}>
                  <Box className="flex items-center mb-2">
                    <Box className="flex items-center justify-start w-full p-2 bg-blue-100 rounded-md">
                      {themeKey === 'tactique' && <SportsSoccerIcon className="mr-2 text-blue-600" fontSize={isMobile ? "medium" : "small"} />}
                      {themeKey === 'technique' && <ChessIcon className="mr-2 text-green-600" fontSize={isMobile ? "medium" : "small"} />}
                      {themeKey === 'athletique' && <FitnessCenterIcon className="mr-2 text-orange-600" fontSize={isMobile ? "medium" : "small"} />}
                      {themeKey === 'mental' && <PsychologyIcon className="mr-2 text-purple-600" fontSize={isMobile ? "medium" : "small"} />}
                      <Typography variant="subtitle2" className="font-medium capitalize text-base sm:text-sm">
                        {themeKey}
                      </Typography>
                    </Box>
                  </Box>
                  {[0, 1].map((tIdx) => (
                    <Box key={tIdx} sx={{ width: '100%' }}>
                      <Box className="w-full pb-2">
                        <FormControl fullWidth>
                          <InputLabel>Thème {tIdx + 1}</InputLabel>
                          <Select
                            value={period.themes[themeKey][tIdx].label}
                            label={`Thème ${tIdx + 1}`}
                            onChange={(e: SelectChangeEvent) => handleThemeChange(pIdx, themeKey, tIdx, 'label', e.target.value)}
                            size={isMobile ? "medium" : "small"}
                            sx={{ 
                              fontSize: isMobile ? '1rem' : 'inherit',
                              height: isMobile ? '56px' : 'inherit'
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: isMobile ? 280 : 300,
                                  width: '90%',
                                },
                              },
                            }}
                          >
                            <MenuItem value=""><em>Aucun</em></MenuItem>
                            {THEME_LABELS[themeKey].map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                            <MenuItem value="__custom__">Autre…</MenuItem>
                          </Select>
                        </FormControl>
                        {period.themes[themeKey][tIdx].label === '__custom__' && (
                          <TextField
                            fullWidth
                            size={isMobile ? "medium" : "small"}
                            placeholder="Thème personnalisé"
                            value={period.themes[themeKey][tIdx].customLabel}
                            onChange={(e) => handleThemeChange(pIdx, themeKey, tIdx, 'customLabel', e.target.value)}
                            className="mt-2"
                            sx={{ 
                              '& .MuiInputBase-root': { 
                                fontSize: isMobile ? '1rem' : 'inherit',
                                height: isMobile ? '56px' : 'inherit'
                              } 
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        ))}

        <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" mt={4} mb={2} width="100%" gap={isMobile ? 2 : 0}>
          <Button
            variant="outlined"
            color="primary"
            onClick={addPeriod}
            startIcon={<AddIcon />}
            fullWidth={isMobile}
            sx={{
              borderRadius: '8px',
              padding: isMobile ? '12px 16px' : '8px 16px',
              fontSize: isMobile ? '1rem' : '0.875rem',
              height: isMobile ? '56px' : 'auto',
              textTransform: 'none',
              order: isMobile ? 2 : 1
            }}
          >
            {isMobile ? 'Ajouter période' : 'Ajouter période'}
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={<SaveIcon />}
            fullWidth={isMobile}
            sx={{
              borderRadius: '8px',
              padding: isMobile ? '12px 16px' : '8px 16px',
              fontSize: isMobile ? '1rem' : '0.875rem',
              height: isMobile ? '56px' : 'auto',
              textTransform: 'none',
              order: isMobile ? 1 : 2
            }}
          >
            Enregistrer
          </Button>
        </Box>
      </form>
    </Paper>

    {/* Dialog de confirmation de suppression */}
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: isMobile ? '8px' : '0'
        }
      }}
    >
      <DialogTitle id="alert-dialog-title">
        Confirmation de suppression
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Êtes-vous sûr de vouloir supprimer cette période ? Cette action est irréversible.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
          Annuler
        </Button>
        <Button
          onClick={() => {
            if (periodToDelete !== null) {
              const newPeriods = [...formData.periods];
              newPeriods.splice(periodToDelete, 1);
              setFormData({ ...formData, periods: newPeriods });
              setDeleteDialogOpen(false);
              setPeriodToDelete(null);
            }
          }}
          color="error"
          autoFocus
        >
          Supprimer
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );

};

export default EditProgrammation;
